export const API_HOST = window.location.origin;
export const API_PATH = "/web/api/v1";
export const API_URL = `${API_HOST}${API_PATH}`;
export const CONTENT_HOST = process.env.CMS_URL;

export const NEW_API_PATH = "/api";
export const NEW_API_URL = `${API_HOST}${NEW_API_PATH}`;

/**
 * Browser key for the Google Maps JS SDK. Set `GOOGLE_MAPS_BROWSER_API_KEY` in
 * `.env` — it ends up in the bundle by design (Google protects this key class
 * with an HTTP-referrer restriction, not with secrecy), so it must be the same
 * referrer-restricted browser key already hardcoded in the legacy flow
 * (`app/views/shared/_javascript.html.haml`), not left unset.
 *
 * Not to be confused with `ENV['GOOGLE_API_KEY']` / `ENV['GOOGLE_MAPS_API_KEY']`:
 * those are server-side keys (geocoding, static maps) and are IP-restricted, so
 * they would be rejected from the browser.
 */
export const GOOGLE_MAPS_BROWSER_API_KEY =
  process.env.GOOGLE_MAPS_BROWSER_API_KEY;

/**
 * The OAuth client identifiers the browser-side SSO SDKs need, and the same ones
 * omniauth signs the legacy pages in with (`config/initializers/omniauth.rb`).
 *
 * Public by design — both providers gate them on authorized origins rather than
 * on secrecy — but neither is set in every environment, so an empty value hides
 * that provider's button instead of opening a popup that cannot succeed.
 */
export const GOOGLE_OAUTH2_CLIENT_ID = process.env.GOOGLE_OAUTH2_CLIENT_ID;
export const FACEBOOK_APP_ID = process.env.FACEBOOK_APP_ID;

/**
 * Apple "Sign in with Apple" web Services ID — the audience of the id_token the
 * popup returns, which `Mobile::NativeAuth` verifies (`os: web`). Not an env
 * var like the two above: it is one fixed identifier across every environment,
 * and it must stay equal to `Rails.application.credentials.apple[:client_id]`,
 * which the endpoint checks the audience against.
 */
export const APPLE_SERVICES_ID = "com.listminut.app";

/**
 * ISO 3166-1 alpha-2 of the country this request belongs to — `"be"` or `"fr"`,
 * the only two Ring Twice runs in.
 *
 * Rails resolves it from the TLD (`CurrentCountryResolver`) and publishes it on
 * a `<meta>` in the React layouts, the same way the CSRF token travels. Asking
 * the API instead is not an option: `API::CountriesController` is a
 * `PrivateController` and the task-creation flow serves logged-out visitors.
 *
 * Read per call rather than frozen at import: Storybook and jest render without
 * the tag, and a module constant would pin them to the fallback even after a
 * test sets one up. The fallback matches `CurrentCountryResolver::DEFAULT_COUNTRY`.
 */
export const DEFAULT_COUNTRY = "be";

export const getCurrentCountry = (): string =>
  document
    .querySelector<HTMLMetaElement>('meta[name="current-country"]')
    ?.content?.trim() || DEFAULT_COUNTRY;

/**
 * Attribution cookie (`know_from`) set by the legacy landing/SEM controllers.
 * It is `HttpOnly` (`config/initializers/secure_headers.rb`), so it travels to
 * React the same way the country does: a `<meta>` tag, not `document.cookie`.
 *
 * No fallback: unlike the country, a blank value here is a valid answer (the
 * visitor came in through no tracked channel), not a rendering gap to paper over.
 */
export const getKnowFrom = (): string | undefined =>
  document
    .querySelector<HTMLMetaElement>('meta[name="know-from"]')
    ?.content?.trim() || undefined;

/**
 * The first screen of the task creation funnel, set by Rails in the session and handed
 * over the same way as `know-from`. Blank on the edit flow, which has no entry door.
 */
export const getTaskEntryDoor = (): string | undefined =>
  document
    .querySelector<HTMLMetaElement>('meta[name="task-entry-door"]')
    ?.content?.trim() || undefined;

export default {
  API_HOST,
  API_PATH,
  API_URL,
  NEW_API_PATH,
  NEW_API_URL,
};
